import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Select,
  Space, Popconfirm, message, Tag, Typography,
  Switch, Row, Col, Card, Avatar
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  UserOutlined, MailOutlined, PhoneOutlined, KeyOutlined
} from '@ant-design/icons';
import AdminService from '../services/adminService';

const { Title, Text } = Typography;

const BusinessStaffManagement = ({ businessId }) => {
  const [staffList, setStaffList] = useState([]);
  const [availableNavItems, setAvailableNavItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form] = Form.useForm();

  // Load staff data on mount or when businessId changes
  useEffect(() => {
    if (businessId) {
      fetchStaff();
    }
    fetchNavItems();
  }, [businessId]);

  const fetchNavItems = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/nav-items/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          }
        }
      );
      const data = await response.json();
      if (response.ok || data.success) {
        // Only keep nav items where source === 'rbo'
        const rboItems = (data.data || data).filter(item => item.source === 'rbo');
        setAvailableNavItems(rboItems);
      }
    } catch (error) {
      console.error('Error fetching nav items:', error);
    }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      // We assume standard Django Rest Framework response or our custom {success, data} wrapper
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-management/${businessId}/staff/`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          }
        }
      );
      const data = await response.json();
      if (response.ok || data.success) {
        setStaffList(data.data || data || []);
      } else {
        message.error(data.message || 'Failed to fetch staff members');
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      message.error('An error occurred while fetching staff');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingStaff(null);
    form.resetFields();
    form.setFieldsValue({ status: true, nav_items: [] });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingStaff(record);
    form.setFieldsValue({
      first_name: record.first_name,
      last_name: record.last_name,
      email: record.email,
      phone: record.phone,
      role: record.role,
      status: record.status !== false, // Default to true if undefined
      password_hash: '', // Leave blank by default on edit
      nav_items: record.nav_items || [],
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (staffId) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-management/${businessId}/staff/${staffId}/`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          }
        }
      );
      if (response.ok) {
        message.success('Staff member deleted successfully');
        fetchStaff();
      } else {
        const data = await response.json();
        message.error(data.message || 'Failed to delete staff member');
      }
    } catch (error) {
      console.error('Error deleting staff:', error);
      message.error('An error occurred while deleting staff');
    }
  };

  const handleModalSubmit = async (values) => {
    setSaving(true);
    try {
      const url = editingStaff
        ? `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-management/${businessId}/staff/${editingStaff.staff_id}/`
        : `${import.meta.env.VITE_API_BASE_URL}/kirazee/api/v1/admin/business-management/${businessId}/staff/`;
      const method = editingStaff ? 'PUT' : 'POST';

      // Prepare payload
      const payload = {
        ...values,
      };

      // If editing and password is not provided, remove it from payload so we don't overwrite it
      if (editingStaff && !payload.password_hash) {
        delete payload.password_hash;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok || data.success) {
        message.success(`Staff member ${editingStaff ? 'updated' : 'added'} successfully!`);
        setIsModalVisible(false);
        fetchStaff();
      } else {
        // Display validation errors if any
        if (data.errors) {
          const errorMsgs = Object.values(data.errors).flat().join(', ');
          message.error(`Validation Error: ${errorMsgs}`);
        } else {
          message.error(data.message || `Failed to ${editingStaff ? 'update' : 'add'} staff member`);
        }
      }
    } catch (error) {
      console.error('Error saving staff:', error);
      message.error('An error occurred while saving staff details');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <Text strong>{record.first_name} {record.last_name}</Text>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => <Tag color="blue">{role || '—'}</Tag>,
    },
    {
      title: 'Contact Info',
      key: 'contact',
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {record.email && (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              <MailOutlined style={{ marginRight: '6px' }} />
              {record.email}
            </Text>
          )}
          {record.phone && (
            <Text type="secondary" style={{ fontSize: '13px' }}>
              <PhoneOutlined style={{ marginRight: '6px' }} />
              {record.phone}
            </Text>
          )}
          {!record.email && !record.phone && '—'}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status ? 'green' : 'red'}>
          {status ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 180,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Staff"
            description={`Are you sure you want to delete ${record.first_name}?`}
            onConfirm={() => handleDelete(record.staff_id)}
            okText="Yes, delete"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={5} style={{ margin: 0 }}>Business Staff</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Staff
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={staffList}
        rowKey="staff_id"
        loading={loading}
        pagination={{ pageSize: 8 }}
        scroll={{ x: 'max-content' }}
        size="middle"
        bordered
      />

      <Modal
        title={
          <Space>
            {editingStaff ? <EditOutlined /> : <PlusOutlined />}
            <span>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</span>
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleModalSubmit} style={{ marginTop: '16px' }}>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                name="first_name"
                label="First Name"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input placeholder="Enter first name" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="last_name" label="Last Name">
                <Input placeholder="Enter last name" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                name="role"
                label="Role"
                rules={[{ required: true, message: 'Role is required' }]}
              >
                <Input placeholder="e.g. Manager, Cashier, Waiter" />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name="status"
                label="Active Status"
                valuePropName="checked"
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item name="email" label="Email Address" rules={[{ type: 'email', message: 'Enter a valid email' }]}>
                <Input placeholder="Enter email" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item name="phone" label="Phone Number" rules={[{ required: true, message: 'Phone is required' }]}>
                <Input placeholder="Enter phone" prefix={<PhoneOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="nav_items"
                label="Assigned Navigation Items"
                extra="Select the menu items this staff member is allowed to access."
              >
                <Select
                  mode="multiple"
                  placeholder="Select navigation items"
                  options={availableNavItems.map(item => ({ label: item.nav_name, value: item.id }))}
                  style={{ width: '100%' }}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="password_hash"
                label={editingStaff ? "New Password (Leave blank to keep current)" : "Password"}
                rules={[{ required: !editingStaff, message: 'Password is required for new staff' }]}
                extra="This will be used for staff login."
              >
                <Input.Password placeholder="Enter password" prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default BusinessStaffManagement;
